import { Plan, PlanRate, PlanContribution, Employee, Dependent, LifeDisabilityCoverageConfig } from "../schema";
import { isLifeDisabilityType, isHealthOrMedicalPlan } from "../schemas/plans";
import { calculateCoverageAmount, calculateMonthlyPremium, EmployeeEarnings } from "./lifeDisabilityCalculations";
import { TABLE_CONTRIBUTION_LABELS, LEGACY_LABEL_MAP, getDependentContributionLabel } from "../constants/contributions";
import { TABLE_RATE_LABELS } from "../constants/coverageTable";
import { narrowRatesToRatingArea } from "./planRateResolution";

export type CoverageTierCanonical =
  | "employee_only"
  | "employee_spouse"
  | "employee_children"
  | "family";

export type MemberType = "employee" | "spouse" | "child";

export type MemberBreakdown = {
  memberType: MemberType;
  age?: number | null;
  ageBandLabel?: string | null;
  monthlyRate: number;
  employeeAmount: number;
  employerAmount: number;
  planRateId?: number | null;
  /** true = enrolled but billed $0 due to the ACA 3-oldest-under-21 child cap. */
  capped?: boolean;
};

export type EnrollmentCalculationInput = {
  plan: Plan;
  planRates: PlanRate[];
  planContributions: PlanContribution[];
  employee: Employee;
  dependents: Dependent[];
  coverageTier?: string;
  ageAsOf?: string | Date;
  allPlanContributions?: PlanContribution[];
  allPlanRates?: PlanRate[];
  lifeDisabilityConfig?: LifeDisabilityCoverageConfig | null;
  employeeCompensation?: { compensationType?: string; compensationAmount?: string | number; salary?: string | number } | null;
  /** The group's rating area (e.g. CA 1..19). When `planRates` still carries
   * several areas' rows, this picks the area; without it an age that prices
   * differently per area is refused with a warning instead of priced at
   * whichever area's row comes first (DEV-315). */
  ratingAreaId?: number | null;
};

export type EnrollmentCalculationResult = {
  coverageTier: CoverageTierCanonical;
  totalMonthlyRate: number;
  employeePremium: number;
  employerPremium: number;
  members: MemberBreakdown[];
  warnings?: string[];
  benefitAmount?: number | null;
  benefitAmountPeriod?: "total" | "monthly" | "weekly" | null;
  contributionResolved: {
    method: "tier_rates" | "age_rates" | "table_rates" | "age_tier_rates" | "life_disability";
    key: string;
    payer?: "employer" | "employee";
    percentage?: number | null;
    nominalAmount?: number | null;
    maxAmount?: number | null;
    fallbackApplied?: boolean;
  } | null;
  /**
   * Tier-rated plans only: the gross monthly rate split into the employee-only
   * portion and the dependent portion (gross dollars, before contribution).
   * Used by the premium-breakdown UI; does not affect any premium total.
   */
  tierBreakdown?: {
    grossTier: number;
    employeeOnlyRate: number;
    dependentPortion: number;
  };
};

const toNumber = (v: any, d = 0): number => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const normalizeRelationship = (rel: any): string =>
  String(rel ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isSpouseRelationship = (rel: any): boolean => {
  const r = normalizeRelationship(rel);
  return (
    r === "spouse" ||
    r === "spouse ex" || // ex-spouse: still spouse-tier (may remain covered)
    r === "domestic partner" ||
    r === "partner" ||
    r === "significant other"
  );
};

export const isChildRelationship = (rel: any): boolean => {
  const r = normalizeRelationship(rel);
  if (r === "dependent") return true;
  if (r === "child") return true;
  if (r.startsWith("child")) return true;
  if (r.includes("child")) return true; // e.g. "stepchild"
  if (r === "son" || r === "daughter") return true;
  return false;
};

export const normalizeCoverageTier = (
  tier: string | undefined | null
): CoverageTierCanonical => {
  const t = String(tier || "").toLowerCase();
  if (t.includes("family")) return "family";
  if (t.includes("spouse")) return "employee_spouse";
  if (t.includes("child")) return "employee_children";
  if (t.includes("children")) return "employee_children";
  if (t.includes("employee_only") || t.includes("employee only"))
    return "employee_only";
  return "employee_only";
};

export const inferTierFromDependents = (
  dependents: Dependent[]
): CoverageTierCanonical => {
  if (!dependents || dependents.length === 0) return "employee_only";
  const hasSpouse = dependents.some((d) =>
    isSpouseRelationship((d as any).relationship)
  );
  const hasChild = dependents.some((d) =>
    isChildRelationship((d as any).relationship)
  );
  if (hasSpouse && hasChild) return "family";
  if (hasSpouse) return "employee_spouse";
  if (hasChild) return "employee_children";
  return "employee_only";
};

const getStructureType = (planRates: PlanRate[]): string | null => {
  const first = (
    planRates && planRates.length > 0 ? (planRates[0] as any) : null
  ) as any;
  return first?.rateStructureType ?? null;
};

// Tier rates can be priced per rating area (DEV-324). Callers narrow to the
// group's area first; if that wasn't possible and the tier's rows still span
// several areas, refuse to guess (same rule as the age lookup).
const findTierRateRow = (
  planRates: PlanRate[],
  coverageTier: CoverageTierCanonical,
  warnings?: string[],
): PlanRate | undefined => {
  const candidates = planRates.filter(
    (r) =>
      String((r as any).rateStructureType) === "tier_rates" &&
      normalizeCoverageTier((r as any).coverageTier) === coverageTier
  );
  return pickUnambiguousRateRow(candidates, `tier ${coverageTier}`, warnings);
};

const parseAge = (
  dob?: string | Date | null,
  asOf?: string | Date | null
): number | null => {
  if (!dob) return null;
  const dt = typeof dob === "string" ? new Date(dob) : (dob as Date);
  if (Number.isNaN(dt.getTime())) return null;
  const ref = asOf
    ? typeof asOf === "string"
      ? new Date(asOf)
      : (asOf as Date)
    : new Date();
  const refValid = Number.isNaN(ref.getTime()) ? new Date() : ref;
  const diffMs = refValid.getTime() - dt.getTime();
  return Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000));
};

const parseAgeBand = (
  band: string
): { min: number; max: number | null } | null => {
  const m = String(band || "")
    .trim()
    .match(/^(\d+)(?:\s*-\s*(\d+|\+))?$/);
  if (!m) return null;
  const min = parseInt(m[1], 10);
  const maxRaw = m[2];
  if (!maxRaw) return { min, max: min };
  if (maxRaw === "+") return { min, max: null };
  return { min, max: parseInt(maxRaw, 10) };
};

/**
 * Contribution fallback when a plan has no configured contribution: employer
 * pays $0 and the employee bears the full premium, until contributions are set.
 * Previously a 70/30 split was assumed, which silently overstated the employer's
 * obligation — manager directive: never default a non-zero employer share.
 */
const zeroEmployerSplit = (
  monthly: number,
): { employee: number; employer: number } => ({
  employee: Math.round(monthly * 100) / 100,
  employer: 0,
});

/**
 * One row out of the candidates that price an age. A multi-region library
 * plan carries every rating area's row for the same band; taking the first
 * silently priced client 219 (CA Area 3) at Area 1 (DEV-315). When the
 * candidates span more than one area, a region-agnostic row wins if there is
 * one; otherwise nothing is chosen and the caller is told why. Single-area
 * candidates behave exactly as before (first match).
 */
const pickUnambiguousRateRow = (
  candidates: PlanRate[],
  /** What is being priced, for the warning: "age 35", "tier employee_only". */
  what: string,
  warnings?: string[],
): PlanRate | undefined => {
  if (candidates.length === 0) return undefined;
  const areas = new Set(
    candidates
      .map((r) => (r as any).ratingAreaId)
      .filter((a) => a != null)
      .map((a) => Number(a)),
  );
  if (areas.size <= 1) return candidates[0];
  const agnostic = candidates.find((r) => (r as any).ratingAreaId == null);
  if (agnostic) return agnostic;
  warnings?.push(
    `Rates for ${what} span ${areas.size} rating areas and no rating area was resolved — no rate applied`,
  );
  return undefined;
};

const rowsContainingAge = (rows: PlanRate[], age: number): PlanRate[] =>
  rows.filter((r) => {
    const band = (r as any).ageBand as string | null;
    if (!band) return false;
    const parsed = parseAgeBand(band);
    if (!parsed) return false;
    return age >= parsed.min && (parsed.max == null || age <= parsed.max);
  });

const findAgeRateRow = (planRates: PlanRate[], age: number, warnings?: string[]): PlanRate | undefined => {
  const rows = planRates.filter(
    (r) =>
      String((r as any).rateStructureType) === "age_rates" ||
      String((r as any).rateStructureType) === "wb_table" ||
      String((r as any).rateStructureType) === "table_rates"
  );
  const exact = rowsContainingAge(rows, age);
  if (exact.length > 0) return pickUnambiguousRateRow(exact, `age ${age}`, warnings);
  // ACA fallback: ages 0-14 share the same rate factor. If no exact match found
  // for an age in this range (e.g., W&B returns band "0" and "14" individually
  // with no entries for 1-13), try the adjacent ACA-equivalent age.
  if (age >= 0 && age <= 14) {
    const fallbackAges = age === 0 ? [14] : [0, 14];
    for (const fa of fallbackAges) {
      const viaFallback = rowsContainingAge(rows, fa);
      if (viaFallback.length > 0) return pickUnambiguousRateRow(viaFallback, `age ${age}`, warnings);
    }
  }
  // Age above every band: insurers cap age rating at the top band (ACA:
  // 64-and-over is flat). Catches plans whose top band was stored as a single
  // age (e.g. "64" instead of "64-99") — a spouse age 67 then prices at the
  // highest band instead of warning "No rate".
  let topMin = -Infinity;
  for (const r of rows) {
    const parsed = parseAgeBand((r as any).ageBand as string | null ?? "");
    if (parsed && parsed.min > topMin) topMin = parsed.min;
  }
  if (topMin !== -Infinity && age > topMin) {
    const topRows = rows.filter((r) => parseAgeBand((r as any).ageBand as string | null ?? "")?.min === topMin);
    return pickUnambiguousRateRow(topRows, `age ${age}`, warnings);
  }
  return undefined;
};

// ACA age-rating child cap (HEALTH/MEDICAL only): carriers may bill at most the
// 3 OLDEST children under age 21. Additional under-21 children are enrolled but
// $0. Children 21+ are each billed at their own age rate and are never capped.
// Returns the set of `members` indices that should bill $0.
const cappedChildMemberIndices = (
  members: Array<{ memberType: MemberType; age: number | null }>,
  planType: string,
): Set<number> => {
  const capped = new Set<number>();
  if (!isHealthOrMedicalPlan(planType)) return capped;
  const under21 = members
    .map((m, idx) => ({ m, idx }))
    .filter(({ m }) => m.memberType === "child" && (m.age ?? 0) < 21);
  // Oldest first; deterministic tie-break by original order.
  under21.sort((a, b) => ((b.m.age ?? 0) - (a.m.age ?? 0)) || (a.idx - b.idx));
  for (const { idx } of under21.slice(3)) capped.add(idx);
  return capped;
};

// Find age-tier rate: matches both age band AND coverage tier
const findAgeTierRateRow = (
  planRates: PlanRate[],
  age: number,
  coverageTier: CoverageTierCanonical
): PlanRate | undefined => {
  const rows = planRates.filter(
    (r) => String((r as any).rateStructureType) === "age_tier_rates"
  );
  for (const r of rows) {
    const band = (r as any).ageBand as string | null;
    const tier = (r as any).coverageTier as string | null;
    if (!band || !tier) continue;
    if (normalizeCoverageTier(tier) !== coverageTier) continue;
    const parsed = parseAgeBand(band);
    if (!parsed) continue;
    const { min, max } = parsed;
    if (age >= min && (max == null || age <= max)) return r;
  }
  return undefined;
};

// Find coverage table rate by label (e.g., "Employee", "Employee + Spouse")
const findCoverageTableRate = (planRates: PlanRate[], label: string): PlanRate | undefined => {
  return planRates.find(
    (r) => {
      const isTableRates = String((r as any).rateStructureType) === "table_rates";
      const coverageTableMatch = String((r as any).coverageTable || "") === label;
      return isTableRates && coverageTableMatch;
    }
  );
};


// Resolve the effective monthly rate: use tobaccoMonthlyRate if tobacco user and rate exists
const resolveMonthlyRate = (rateRow: any, isTobacco: boolean): number => {
  if (isTobacco) {
    const tobaccoRate = toNumber(rateRow?.tobaccoMonthlyRate, NaN);
    if (Number.isFinite(tobaccoRate) && tobaccoRate > 0) return tobaccoRate;
  }
  return toNumber(rateRow?.monthlyRate, 0);
};

// Helper to find contribution row, with backward compatibility for old labels
const findTableContributionRow = (
  planContributions: PlanContribution[],
  label: string
): PlanContribution | null => {
  // Try new format first
  let contrib = (planContributions as any[]).find(
    (c) =>
      String((c as any).contributionType) === "table_rates" &&
      String((c as any).contributionTypeValue) === label
  );
  if (contrib) return contrib as PlanContribution;

  // Backward compatibility: try old format if new format not found
  const oldLabels = LEGACY_LABEL_MAP[label] || [];
  for (const oldLabel of oldLabels) {
    contrib = (planContributions as any[]).find(
      (c) =>
        String((c as any).contributionType) === "table_rates" &&
        String((c as any).contributionTypeValue) === oldLabel
    );
    if (contrib) return contrib as PlanContribution;
  }
  return null;
};

const deriveAmountsFromContribution = (
  monthlyRate: number,
  row: Partial<PlanContribution> | null | undefined
): { employee: number; employer: number } | null => {
  if (!row) return null;
  // Live percentage/nominal wins over stored employeeAmount/employerAmount —
  // those are save-time dollars that go stale when rates change. Stored
  // dollars apply only to amounts-only rows (EASE imports have no percentage).
  const employeeAmount = toNumber((row as any).employeeAmount, NaN);
  const employerAmount = toNumber((row as any).employerAmount, NaN);
  const hasLiveTerms =
    ((row as any).percentage != null && Number.isFinite(toNumber((row as any).percentage, NaN))) ||
    ((row as any).nominalAmount != null && Number.isFinite(toNumber((row as any).nominalAmount, NaN)));
  if (!hasLiveTerms && (Number.isFinite(employeeAmount) || Number.isFinite(employerAmount))) {
    const er = Number.isFinite(employerAmount)
      ? employerAmount
      : Math.max(0, monthlyRate - employeeAmount);
    const ee = Number.isFinite(employeeAmount)
      ? employeeAmount
      : Math.max(0, monthlyRate - er);
    return { employee: ee, employer: er };
  }

  const payer = (row as any).payer as "employer" | "employee" | undefined;
  const percentage =
    (row as any).percentage != null ? toNumber((row as any).percentage, NaN) : NaN;
  const nominal =
    (row as any).nominalAmount != null ? toNumber((row as any).nominalAmount, NaN) : NaN;
  const maxAmount =
    (row as any).maxAmount != null ? toNumber((row as any).maxAmount, NaN) : NaN;

  let er = 0;
  let ee = 0;
  if (Number.isFinite(percentage)) {
    const portion = (monthlyRate * (percentage as number)) / 100;
    if (payer === "employer") {
      er = portion;
      ee = Math.max(0, monthlyRate - portion);
    } else {
      ee = portion;
      er = Math.max(0, monthlyRate - portion);
    }
  } else if (Number.isFinite(nominal)) {
    if (payer === "employer") {
      er = nominal as number;
      ee = Math.max(0, monthlyRate - er);
    } else {
      ee = nominal as number;
      er = Math.max(0, monthlyRate - ee);
    }
  } else {
    return null;
  }

  if (Number.isFinite(maxAmount)) {
    if (payer === "employer") {
      er = Math.min(er, maxAmount as number);
      ee = Math.max(0, monthlyRate - er);
    } else {
      ee = Math.min(ee, maxAmount as number);
      er = Math.max(0, monthlyRate - ee);
    }
  }

  return {
    employee: Math.round(ee * 100) / 100,
    employer: Math.round(er * 100) / 100,
  };
};

const pickContributionForRate = (
  planContributions: PlanContribution[],
  planRate: PlanRate,
  fallbackKey?: { type: "tier_rates" | "age_rates"; value: string }
): Partial<PlanContribution> | null => {
  const exact = planContributions.find(
    (c) => (c as any).planRateId === (planRate as any).id
  );
  if (exact) return exact;
  if (!fallbackKey) return null;
  const generic = planContributions.find(
    (c) =>
      String((c as any).contributionType) === fallbackKey.type &&
      String((c as any).contributionTypeValue) === fallbackKey.value
  );
  return generic || null;
};

export function formatBenefitAmount(amount: number, period: "total" | "monthly" | "weekly" | null | undefined): string {
  const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  if (period === "monthly") return `${fmt.format(amount)}/mo`;
  if (period === "weekly") return `${fmt.format(amount)}/wk`;
  return fmt.format(amount);
}

export function calculateEnrollmentPremium(
  input: EnrollmentCalculationInput
): EnrollmentCalculationResult {
  const warnings: string[] = [];
  const result = _calculateEnrollmentPremiumCore(input, warnings);
  const pt = String((input.plan as any)?.type || (input.plan as any)?.planType || "");
  if (isLifeDisabilityType(pt) && input.lifeDisabilityConfig === null) {
    warnings.push("Coverage not configured for this plan");
  }
  if (warnings.length > 0) {
    result.warnings = [...(result.warnings || []), ...warnings];
  }
  return result;
}

function _calculateEnrollmentPremiumCore(
  input: EnrollmentCalculationInput,
  warnings: string[] = [],
): EnrollmentCalculationResult {
  const { planContributions: rawPlanContributions, employee, dependents, ageAsOf } = input;
  // The group's rating area narrows the candidate rows up front (the same
  // rule the server and /api/plan-rates/bulk apply), so a multi-region plan
  // is priced at the caller's area — never at whichever area's row is first.
  const planRates = narrowRatesToRatingArea(input.planRates || [], input.ratingAreaId);
  const planName = String((input.plan as any)?.name || (input.plan as any)?.id || "unknown");

  // Detect base plan reference: if this plan's contributions contain payer="base",
  // we need the base plan's rates and contributions to calculate employer dollar amounts
  let planContributions = rawPlanContributions;
  let basePlanInfo: {
    basePlanId: number;
    baseContributions: PlanContribution[];
    basePlanRates: PlanRate[];
  } | null = null;

  if (Array.isArray(rawPlanContributions)) {
    const baseRef = (rawPlanContributions as any[]).find(
      (c) => String((c as any).payer) === "base" && (c as any).basePlanId
    );
    if (baseRef && input.allPlanContributions) {
      const basePlanId = Number((baseRef as any).basePlanId);
      const baseContribs = (input.allPlanContributions as any[]).filter(
        (c) => Number((c as any).planId) === basePlanId && String((c as any).payer) !== "base"
      );
      if (baseContribs.length > 0) {
        // Use base plan's contribution rows for contribution lookup
        planContributions = baseContribs as PlanContribution[];
        // If we have base plan rates, enable dollar-amount calculation
        const baseRates = input.allPlanRates
          ? narrowRatesToRatingArea(
              (input.allPlanRates as any[]).filter((r) => Number((r as any).planId) === basePlanId),
              input.ratingAreaId,
            )
          : [];
        if (baseRates.length > 0) {
          basePlanInfo = {
            basePlanId,
            baseContributions: baseContribs as PlanContribution[],
            basePlanRates: baseRates as PlanRate[],
          };
        } else {
          warnings.push(`Base plan ${basePlanId} rates not found — contribution amounts may be incorrect`);
        }
      } else {
        warnings.push(`Base plan ${basePlanId} contributions not found — using default split`);
      }
    }
  }

  // Life/Disability composite rate path
  const planType = String((input.plan as any)?.type || (input.plan as any)?.planType || "");
  const ldConfig = input.lifeDisabilityConfig;

  // Check if employee is a tobacco user
  const isTobaccoUser = String((employee as any).tobaccoUser || "").toLowerCase() === "yes";

  if (isLifeDisabilityType(planType) && ldConfig && String(ldConfig.rateType || "composite") === "composite") {
    const warnings: string[] = [];
    // Use tobacco rate if employee is tobacco user and tobacco distinction is enabled
    const useTobaccoRate = isTobaccoUser && (ldConfig as any).tobaccoDistinction && toNumber((ldConfig as any).tobaccoRate, 0) > 0;
    const ratePerUnit = useTobaccoRate ? toNumber((ldConfig as any).tobaccoRate, 0) : toNumber(ldConfig.ratePerUnit, 0);
    const rateUnitBasis = toNumber(ldConfig.rateUnitBasis, 1000);

    // Build employee earnings from compensation data
    const comp = input.employeeCompensation;
    let baseSalary = 0;
    if (comp) {
      const raw = toNumber((comp as any).compensationAmount, 0) || toNumber((comp as any).salary, 0);
      if (raw > 0) {
        // Annualize hourly wages
        const compType = String((comp as any).compensationType || "").toLowerCase();
        baseSalary = compType === "hourly" ? raw * 2080 : raw;
      }
    }

    const isEarningsBased = ldConfig.coverageAmountType === "earnings_multiple" || ldConfig.coverageAmountType === "earnings_percentage";
    if (isEarningsBased && baseSalary <= 0) {
      warnings.push("Employee earnings not available — update compensation to calculate premium");
    }

    const earnings: EmployeeEarnings = { baseSalary };
    const normalizedPlanType = planType.toLowerCase().trim();
    const isDisability = ["std", "ltd", "disability"].includes(normalizedPlanType);
    // For disability plans, maximumCoverage (annual cap) doesn't apply —
    // maxMonthlyBenefit is the correct cap, applied after monthly conversion
    const ldConfigForCoverage = isDisability
      ? { ...ldConfig, maximumCoverage: null } as typeof ldConfig
      : ldConfig;
    const coverageResult = calculateCoverageAmount(ldConfigForCoverage, earnings);
    const configRateBasis = String((ldConfig as any).rateBasis || "benefit_amount");

    let rateBase: number;
    if (!isDisability) {
      // Life: rate per $X of total coverage
      rateBase = coverageResult.finalCoverageAmount;
    } else if (configRateBasis === "earnings_amount") {
      // Disability rated on monthly SALARY (payroll), not benefit
      const earningsUsed = coverageResult.earningsUsed || baseSalary;
      rateBase = earningsUsed / 12;
    } else {
      // Disability rated on monthly BENEFIT (default)
      let monthlyBenefit = coverageResult.finalCoverageAmount / 12;
      const maxMonthly = toNumber((ldConfig as any).maxMonthlyBenefit, 0);
      if (maxMonthly > 0 && monthlyBenefit > maxMonthly) {
        monthlyBenefit = maxMonthly;
      }
      rateBase = monthlyBenefit;
    }

    const monthly = calculateMonthlyPremium(rateBase, ratePerUnit, rateUnitBasis);
    const monthlyRounded = Math.round(monthly * 100) / 100;

    // Contribution split
    let employeePremium = 0;
    let employerPremium = 0;

    // Try planContributions first
    const contribRow = Array.isArray(rawPlanContributions) && rawPlanContributions.length > 0
      ? (rawPlanContributions as any[])[0]
      : null;
    const derived = deriveAmountsFromContribution(monthlyRounded, contribRow);

    if (derived) {
      employeePremium = derived.employee;
      employerPremium = derived.employer;
    } else {
      // Default: voluntary_life → 100% employee; else 100% employer
      const normalized = planType.toLowerCase().trim();
      if (normalized === "voluntary_life") {
        employeePremium = monthlyRounded;
        employerPremium = 0;
      } else {
        employeePremium = 0;
        employerPremium = monthlyRounded;
      }
    }

    const tier = input.coverageTier
      ? normalizeCoverageTier(input.coverageTier)
      : "employee_only" as CoverageTierCanonical;

    // Compute benefit amount for display
    let benefitAmount: number | null = null;
    let benefitAmountPeriod: "total" | "monthly" | "weekly" | null = null;
    if (coverageResult.finalCoverageAmount > 0) {
      if (isDisability) {
        let monthlyBenefit = coverageResult.finalCoverageAmount / 12;
        const maxMonthly = toNumber((ldConfig as any).maxMonthlyBenefit, 0);
        if (maxMonthly > 0 && monthlyBenefit > maxMonthly) {
          monthlyBenefit = maxMonthly;
          warnings.push(`Monthly benefit capped at ${formatBenefitAmount(maxMonthly, "monthly")}`);
        }

        if (normalizedPlanType === "std") {
          const benefitSchedule = String((ldConfig as any)?.benefitSchedule || "weekly").toLowerCase();
          if (benefitSchedule === "monthly") {
            benefitAmount = Math.round(monthlyBenefit);
            benefitAmountPeriod = "monthly";
          } else {
            benefitAmount = Math.round(monthlyBenefit * 12 / 52);
            benefitAmountPeriod = "weekly";
          }
        } else {
          benefitAmount = Math.round(monthlyBenefit);
          benefitAmountPeriod = "monthly";
        }
      } else {
        // life, voluntary_life
        benefitAmount = Math.round(coverageResult.finalCoverageAmount);
        benefitAmountPeriod = "total";
      }
    }

    return {
      coverageTier: tier,
      totalMonthlyRate: monthlyRounded,
      employeePremium: Math.round(employeePremium * 100) / 100,
      employerPremium: Math.round(employerPremium * 100) / 100,
      members: [{
        memberType: "employee" as MemberType,
        age: parseAge((employee as any).dateOfBirth as any, ageAsOf),
        ageBandLabel: null,
        monthlyRate: monthlyRounded,
        employeeAmount: Math.round(employeePremium * 100) / 100,
        employerAmount: Math.round(employerPremium * 100) / 100,
        planRateId: null,
      }],
      ...(warnings.length > 0 ? { warnings } : {}),
      benefitAmount,
      benefitAmountPeriod,
      contributionResolved: {
        method: "life_disability" as const,
        key: planType,
      },
    };
  }

  const structureType = getStructureType(planRates);
  let tier = input.coverageTier
    ? normalizeCoverageTier(input.coverageTier)
    : inferTierFromDependents(dependents);
  if (Array.isArray(dependents) && dependents.length > 0 && tier === "employee_only") {
    tier = inferTierFromDependents(dependents);
  }

  // Table-level contribution path (age/table rates)
  const hasTableContrib = Array.isArray(planContributions)
    ? (planContributions as any[]).some(
        (c) => String((c as any).contributionType) === "table_rates"
      )
    : false;
  if (
    hasTableContrib &&
    (structureType === "age_rates" ||
      structureType === "wb_table" ||
      structureType === "table_rates")
  ) {
    const members: Array<{ memberType: MemberType; age: number | null }> = [];
    members.push({
      memberType: "employee",
      age: parseAge((employee as any).dateOfBirth as any, ageAsOf),
    });
    const spouse = dependents.find((d) => isSpouseRelationship((d as any).relationship));
    if (spouse)
      members.push({
        memberType: "spouse",
        age: parseAge((spouse as any).dateOfBirth as any, ageAsOf),
      });
    const kids = dependents.filter((d) => isChildRelationship((d as any).relationship));
    for (const child of kids)
      members.push({
        memberType: "child",
        age: parseAge((child as any).dateOfBirth as any, ageAsOf),
      });

    // Check if this plan uses coverage table methodology (has coverage tier labels like "Employee", "Employee + Spouse")
    const hasCoverageTableRates = planRates.some(
      (r) =>
        String((r as any).rateStructureType) === "table_rates" &&
        (TABLE_RATE_LABELS as readonly string[]).includes(String((r as any).coverageTable || ""))
    );

    const perMemberRates: Array<{
      type: MemberType;
      monthly: number;
      planRateId?: number | null;
      age: number | null;
      ageBand?: string | null;
      billable?: boolean; // false = enrolled but $0 (ACA under-21 child cap); undefined = billed
    }> = [];

    if (hasCoverageTableRates) {
      // Coverage table methodology: rates are stored per coverage tier (e.g., "Employee" = $200, "Employee + Spouse" = $350)
      // Employee gets rate from "Employee" row
      // For dependents, we calculate from tier differences
      const employeeRateRow = findCoverageTableRate(planRates, "Employee");
      const employeeRate = employeeRateRow ? toNumber((employeeRateRow as any).monthlyRate, 0) : 0;

      // Determine coverage tier for dependents
      const spouseCount = members.filter((m) => m.memberType === "spouse").length;
      const childCount = members.filter((m) => m.memberType === "child").length;
      
      let tierLabel = "Employee";
      if (spouseCount > 0 && childCount >= 3) {
        tierLabel = "Employee + 3+ Children + Spouse";
      } else if (spouseCount > 0 && childCount === 2) {
        tierLabel = "Employee + 2 Children + Spouse";
      } else if (spouseCount > 0 && childCount === 1) {
        tierLabel = "Employee + 1 Children + Spouse";
      } else if (spouseCount > 0) {
        tierLabel = "Employee + Spouse";
      } else if (childCount >= 3) {
        tierLabel = "Employee + 3+ Children";
      } else if (childCount === 2) {
        tierLabel = "Employee + 2 Children";
      } else if (childCount === 1) {
        tierLabel = "Employee + 1 Children";
      }

      const tierRateRow = findCoverageTableRate(planRates, tierLabel);
      const tierTotal = tierRateRow ? toNumber((tierRateRow as any).monthlyRate, 0) : employeeRate;
      // Coverage table rates are TOTAL rates per tier
      const dependentTotal = tierTotal - employeeRate;
      const dependentCount = spouseCount + childCount;

      for (const m of members) {
        let monthly = 0;
        let rateRow = null;
        
        if (m.memberType === "employee") {
          monthly = employeeRate;
          rateRow = employeeRateRow;
        } else {
          // Split dependent total equally among dependents
          monthly = dependentCount > 0 ? dependentTotal / dependentCount : 0;
          rateRow = tierRateRow;
        }

        perMemberRates.push({
          type: m.memberType,
          monthly,
          planRateId: (rateRow as any)?.id ?? null,
          age: m.age,
          ageBand: (rateRow as any)?.ageBand ?? (rateRow as any)?.coverageTable ?? null,
        });
      }
    } else {
      // Age-based methodology: look up rates by age band.
      const cappedIdx = cappedChildMemberIndices(members, planType);
      members.forEach((m, idx) => {
        const age = m.age ?? 0;
        const rateRow = findAgeRateRow(planRates, age, warnings);
        if (!rateRow) warnings.push(`No rate for ${m.memberType} age ${age} on ${planName}`);
        const billable = !cappedIdx.has(idx);
        // Capped children stay enrolled (keep planRateId/age/ageBand) but bill $0.
        const monthly = billable && rateRow ? resolveMonthlyRate(rateRow, m.memberType === "employee" && isTobaccoUser) : 0;
        perMemberRates.push({
          type: m.memberType,
          monthly,
          planRateId: (rateRow as any)?.id ?? null,
          age,
          ageBand: (rateRow as any)?.ageBand ?? null,
          billable,
        });
      });
    }

    const totalMonthly = perMemberRates.reduce((s, r) => s + r.monthly, 0);

    // Apply contributions separately to employee vs dependents
    const employeeMember = perMemberRates.find((r) => r.type === "employee");
    const employeeRate = employeeMember?.monthly || 0;
    const spouseMembers = perMemberRates.filter((r) => r.type === "spouse");
    const childMembers = perMemberRates.filter((r) => r.type === "child");
    const spouseCount = spouseMembers.length;
    const childCount = childMembers.length;

    // Determine which dependent contribution row to use
    const dependentLabel = getDependentContributionLabel(spouseCount, childCount);

    const dependentRate = perMemberRates
      .filter((r) => r.type !== "employee")
      .reduce((sum, r) => sum + r.monthly, 0);

    let employeeDerived: { employee: number; employer: number };
    let dependentDerived: { employee: number; employer: number };

    if (basePlanInfo) {
      // BASE PLAN: Employer pays the same DOLLAR AMOUNT as the base plan.
      // Employee pays the DIFFERENCE (current plan rate - base employer amount).
      // Floor at $0 — excess does NOT carry over to reduce dependent cost.

      // Employee: look up base plan rate for employee's age, apply base contribution %
      const baseEeRateRow = findAgeRateRow(basePlanInfo.basePlanRates, employeeMember?.age ?? 0, warnings);
      const baseEeMonthly = baseEeRateRow ? toNumber((baseEeRateRow as any).monthlyRate, 0) : 0;
      const baseEeContrib = findTableContributionRow(basePlanInfo.baseContributions, TABLE_CONTRIBUTION_LABELS.EMPLOYEE);
      const baseEeDerived = deriveAmountsFromContribution(baseEeMonthly, baseEeContrib);
      const baseEmployerForEe = baseEeDerived ? baseEeDerived.employer : 0;

      employeeDerived = {
        employer: Math.round(Math.min(baseEmployerForEe, employeeRate) * 100) / 100,
        employee: Math.round(Math.max(0, employeeRate - baseEmployerForEe) * 100) / 100,
      };

      // Dependents: look up base plan rate for each dependent, apply base contribution %
      // to the total base dependent rate, then use that dollar amount for current plan
      let baseDependentTotal = 0;
      for (const m of perMemberRates.filter((r) => r.type !== "employee" && r.billable !== false)) {
        const baseRateRow = findAgeRateRow(basePlanInfo.basePlanRates, m.age ?? 0, warnings);
        baseDependentTotal += baseRateRow ? toNumber((baseRateRow as any).monthlyRate, 0) : 0;
      }
      const baseDepContrib = dependentLabel
        ? findTableContributionRow(basePlanInfo.baseContributions, dependentLabel)
        : null;
      const baseDepDerived = baseDependentTotal > 0 && baseDepContrib
        ? deriveAmountsFromContribution(baseDependentTotal, baseDepContrib)
        : null;
      const baseEmployerForDep = baseDepDerived ? baseDepDerived.employer : 0;

      dependentDerived = dependentLabel && dependentRate > 0
        ? {
            employer: Math.round(Math.min(baseEmployerForDep, dependentRate) * 100) / 100,
            employee: Math.round(Math.max(0, dependentRate - baseEmployerForDep) * 100) / 100,
          }
        : { employee: 0, employer: 0 };
    } else {
      // Standard contribution: apply this plan's contribution percentages to this plan's rates
      const employeeContribRow = findTableContributionRow(planContributions, TABLE_CONTRIBUTION_LABELS.EMPLOYEE);
      const eeDerived = deriveAmountsFromContribution(employeeRate, employeeContribRow);
      if (!eeDerived) warnings.push(`No contribution matched '${TABLE_CONTRIBUTION_LABELS.EMPLOYEE}' — employer defaulted to $0 until contributions are configured`);
      employeeDerived = eeDerived || zeroEmployerSplit(employeeRate);

      const dependentContribRow = dependentLabel
        ? findTableContributionRow(planContributions, dependentLabel)
        : null;
      if (dependentLabel && dependentRate > 0) {
        const depDerived = deriveAmountsFromContribution(dependentRate, dependentContribRow);
        if (!depDerived) warnings.push(`No contribution matched '${dependentLabel}' — employer defaulted to $0 until contributions are configured`);
        dependentDerived = depDerived || zeroEmployerSplit(dependentRate);
      } else {
        dependentDerived = { employee: 0, employer: 0 };
      }
    }

    // 3. Combine employee and dependent contributions
    const totalEmployeeAmount = employeeDerived.employee + dependentDerived.employee;
    const totalEmployerAmount = employeeDerived.employer + dependentDerived.employer;

    // 4. Split contributions proportionally across members
    const membersOut: MemberBreakdown[] = perMemberRates.map((r) => {
      let employeeAmount = 0;
      let employerAmount = 0;

      if (r.type === "employee") {
        // Employee uses employee contribution directly
        employeeAmount = employeeDerived.employee;
        employerAmount = employeeDerived.employer;
      } else {
        // Dependents split the dependent contribution proportionally
        if (dependentRate > 0) {
          const ratio = r.monthly / dependentRate;
          employeeAmount = Math.round(dependentDerived.employee * ratio * 100) / 100;
          employerAmount = Math.round(dependentDerived.employer * ratio * 100) / 100;
        }
      }

      return {
        memberType: r.type,
        age: r.age,
        ageBandLabel: r.ageBand ?? null,
        monthlyRate: r.monthly,
        employeeAmount,
        employerAmount,
        planRateId: r.planRateId ?? null,
        capped: r.billable === false,
      };
    });

    // Use the dependent label for contributionResolved (or employee if no dependents)
    const resolvedLabel = dependentLabel || TABLE_CONTRIBUTION_LABELS.EMPLOYEE;
    const resolvedContribRow = findTableContributionRow(planContributions, resolvedLabel);

    return {
      coverageTier: tier,
      totalMonthlyRate: Math.round(totalMonthly * 100) / 100,
      employeePremium: Math.round(totalEmployeeAmount * 100) / 100,
      employerPremium: Math.round(totalEmployerAmount * 100) / 100,
      members: membersOut,
      contributionResolved: resolvedContribRow
        ? {
            method: "table_rates",
            key: resolvedLabel,
            payer: (resolvedContribRow as any).payer,
            percentage: (resolvedContribRow as any).percentage ?? null,
            nominalAmount: (resolvedContribRow as any).nominalAmount ?? null,
            maxAmount: (resolvedContribRow as any).maxAmount ?? null,
          }
        : null,
    };
  }

  // Tier-level contribution path (age/table rates)
  const hasTierContrib = Array.isArray(planContributions)
    ? (planContributions as any[]).some(
        (c) => String((c as any).contributionType) === "tier_rates"
      )
    : false;
  if (
    hasTierContrib &&
    (structureType === "age_rates" ||
      structureType === "wb_table" ||
      structureType === "table_rates")
  ) {
    const members: Array<{ memberType: MemberType; age: number | null }> = [];
    members.push({
      memberType: "employee",
      age: parseAge((employee as any).dateOfBirth as any, ageAsOf),
    });
    const spouse = dependents.find((d) => isSpouseRelationship((d as any).relationship));
    if (spouse)
      members.push({
        memberType: "spouse",
        age: parseAge((spouse as any).dateOfBirth as any, ageAsOf),
      });
    const kids = dependents.filter((d) => isChildRelationship((d as any).relationship));
    for (const child of kids)
      members.push({
        memberType: "child",
        age: parseAge((child as any).dateOfBirth as any, ageAsOf),
      });

    const perMemberRates: Array<{
      type: MemberType;
      monthly: number;
      planRateId?: number | null;
      age: number | null;
      ageBand?: string | null;
      billable?: boolean;
    }> = [];
    const cappedIdx = cappedChildMemberIndices(members, planType);
    members.forEach((m, idx) => {
      const age = m.age ?? 0;
      const rateRow = findAgeRateRow(planRates, age, warnings);
      if (!rateRow) warnings.push(`No rate for ${m.memberType} age ${age} on ${planName}`);
      const billable = !cappedIdx.has(idx);
      const monthly = billable && rateRow ? resolveMonthlyRate(rateRow, m.memberType === "employee" && isTobaccoUser) : 0;
      perMemberRates.push({
        type: m.memberType,
        monthly,
        planRateId: (rateRow as any)?.id ?? null,
        age,
        ageBand: (rateRow as any)?.ageBand ?? null,
        billable,
      });
    });

    const totalMonthly = perMemberRates.reduce((s, r) => s + r.monthly, 0);
    const contribRow = (planContributions as any[]).find(
      (c) =>
        String((c as any).contributionType) === "tier_rates" &&
        normalizeCoverageTier(String((c as any).contributionTypeValue)) === tier
    ) as any;

    let derived: { employee: number; employer: number };
    if (basePlanInfo) {
      // BASE PLAN: Calculate employer $ from base plan rates, apply to current plan total
      let baseTotalMonthly = 0;
      members.forEach((m, idx) => {
        if (cappedIdx.has(idx)) return; // capped children bill $0 on both plans
        const age = m.age ?? 0;
        const baseRateRow = findAgeRateRow(basePlanInfo.basePlanRates, age, warnings);
        baseTotalMonthly += baseRateRow ? toNumber((baseRateRow as any).monthlyRate, 0) : 0;
      });
      const baseContribRow = (basePlanInfo.baseContributions as any[]).find(
        (c: any) =>
          String(c.contributionType) === "tier_rates" &&
          normalizeCoverageTier(String(c.contributionTypeValue)) === tier
      );
      const baseDerived = deriveAmountsFromContribution(baseTotalMonthly, baseContribRow);
      const baseEmployer = baseDerived ? baseDerived.employer : 0;
      derived = {
        employer: Math.round(Math.min(baseEmployer, totalMonthly) * 100) / 100,
        employee: Math.round(Math.max(0, totalMonthly - baseEmployer) * 100) / 100,
      };
    } else {
      const tierDerived = deriveAmountsFromContribution(totalMonthly, contribRow);
      if (!tierDerived) warnings.push(`No contribution matched tier '${tier}' — employer defaulted to $0 until contributions are configured`);
      derived = tierDerived || zeroEmployerSplit(totalMonthly);
    }

    const membersOut: MemberBreakdown[] = perMemberRates.map((r) => {
      const ratio = totalMonthly > 0 ? r.monthly / totalMonthly : 0;
      return {
        memberType: r.type,
        age: r.age,
        ageBandLabel: r.ageBand ?? null,
        monthlyRate: r.monthly,
        employeeAmount: Math.round(derived.employee * ratio * 100) / 100,
        employerAmount: Math.round(derived.employer * ratio * 100) / 100,
        planRateId: r.planRateId ?? null,
        capped: r.billable === false,
      };
    });

    return {
      coverageTier: tier,
      totalMonthlyRate: Math.round(totalMonthly * 100) / 100,
      employeePremium: Math.round(derived.employee * 100) / 100,
      employerPremium: Math.round(derived.employer * 100) / 100,
      members: membersOut,
      contributionResolved: contribRow
        ? {
            method: "tier_rates",
            key: tier,
            payer: (contribRow as any).payer,
            percentage: (contribRow as any).percentage ?? null,
            nominalAmount: (contribRow as any).nominalAmount ?? null,
            maxAmount: (contribRow as any).maxAmount ?? null,
          }
        : null,
    };
  }

  // Age-tier matrix path: single lookup by employee age + elected tier
  if (structureType === "age_tier_rates") {
    const employeeAge = parseAge((employee as any).dateOfBirth as any, ageAsOf) ?? 0;
    const rateRow = findAgeTierRateRow(planRates, employeeAge, tier);
    const monthly = rateRow ? resolveMonthlyRate(rateRow, isTobaccoUser) : 0;

    // Use tier-based contribution if available (same approach as tier_rates)
    const contribRow = (planContributions as any[]).find(
      (c) =>
        String((c as any).contributionType) === "tier_rates" &&
        normalizeCoverageTier(String((c as any).contributionTypeValue)) === tier
    ) as any;
    const derived = deriveAmountsFromContribution(monthly, contribRow) || zeroEmployerSplit(monthly);

    return {
      coverageTier: tier,
      totalMonthlyRate: Math.round(monthly * 100) / 100,
      employeePremium: Math.round(derived.employee * 100) / 100,
      employerPremium: Math.round(derived.employer * 100) / 100,
      members: [
        {
          memberType: "employee",
          age: employeeAge,
          ageBandLabel: (rateRow as any)?.ageBand ?? null,
          monthlyRate: monthly,
          employeeAmount: Math.round(derived.employee * 100) / 100,
          employerAmount: Math.round(derived.employer * 100) / 100,
          planRateId: (rateRow as any)?.id ?? null,
        },
      ],
      contributionResolved: contribRow
        ? {
            method: "age_tier_rates" as const,
            key: tier,
            payer: (contribRow as any).payer,
            percentage: (contribRow as any).percentage ?? null,
            nominalAmount: (contribRow as any).nominalAmount ?? null,
            maxAmount: (contribRow as any).maxAmount ?? null,
          }
        : null,
    };
  }

  // Tier rates base path
  if (structureType === "tier_rates") {
    const row = findTierRateRow(planRates, tier, warnings);
    const monthly = row ? toNumber((row as any).monthlyRate, 0) : 0;

    // Table-method contributions over a tier-rated plan: the "Employee" row
    // applies to the employee-only rate; the dependent-combination row applies
    // to the dependent portion (elected tier rate − employee-only rate).
    // Spec: Contribution Scenarios.xlsx; golden-tested in premiumMatrix.test.ts.
    const hasTableContribRows = Array.isArray(planContributions)
      ? (planContributions as any[]).some(
          (c) => String((c as any).contributionType) === "table_rates"
        )
      : false;
    if (hasTableContribRows && row) {
      const employeeOnlyRow = findTierRateRow(planRates, "employee_only");
      const employeeOnlyRate = employeeOnlyRow
        ? toNumber((employeeOnlyRow as any).monthlyRate, 0)
        : monthly;
      const dependentPortion = Math.max(0, monthly - employeeOnlyRate);

      const spouseCount = dependents.some((d) => isSpouseRelationship((d as any).relationship)) ? 1 : 0;
      const childCount = dependents.filter((d) => isChildRelationship((d as any).relationship)).length;
      const dependentLabel = getDependentContributionLabel(spouseCount, childCount);

      const eeContribRow = findTableContributionRow(planContributions, TABLE_CONTRIBUTION_LABELS.EMPLOYEE);
      const eeDerived = deriveAmountsFromContribution(employeeOnlyRate, eeContribRow);
      if (!eeDerived) warnings.push(`No contribution matched '${TABLE_CONTRIBUTION_LABELS.EMPLOYEE}' — employer defaulted to $0 until contributions are configured`);
      const employeeDerived = eeDerived || zeroEmployerSplit(employeeOnlyRate);

      let dependentDerived = { employee: 0, employer: 0 };
      if (dependentLabel && dependentPortion > 0) {
        const depContribRow = findTableContributionRow(planContributions, dependentLabel);
        const depDerived = deriveAmountsFromContribution(dependentPortion, depContribRow);
        if (!depDerived) warnings.push(`No contribution matched '${dependentLabel}' — employer defaulted to $0 until contributions are configured`);
        dependentDerived = depDerived || zeroEmployerSplit(dependentPortion);
      }

      const employeeTotal = employeeDerived.employee + dependentDerived.employee;
      const employerTotal = employeeDerived.employer + dependentDerived.employer;
      const resolvedLabel = dependentLabel || TABLE_CONTRIBUTION_LABELS.EMPLOYEE;
      const resolvedContribRow = findTableContributionRow(planContributions, resolvedLabel);

      return {
        coverageTier: tier,
        totalMonthlyRate: Math.round(monthly * 100) / 100,
        employeePremium: Math.round(employeeTotal * 100) / 100,
        employerPremium: Math.round(employerTotal * 100) / 100,
        members: [
          {
            memberType: "employee",
            age: parseAge((employee as any).dateOfBirth as any, ageAsOf),
            ageBandLabel: null,
            monthlyRate: monthly,
            employeeAmount: Math.round(employeeTotal * 100) / 100,
            employerAmount: Math.round(employerTotal * 100) / 100,
            planRateId: (row as any)?.id ?? null,
          },
        ],
        tierBreakdown: {
          grossTier: Math.round(monthly * 100) / 100,
          employeeOnlyRate: Math.round(employeeOnlyRate * 100) / 100,
          dependentPortion: Math.round(dependentPortion * 100) / 100,
        },
        contributionResolved: resolvedContribRow
          ? {
              method: "table_rates",
              key: resolvedLabel,
              payer: (resolvedContribRow as any).payer,
              percentage: (resolvedContribRow as any).percentage ?? null,
              nominalAmount: (resolvedContribRow as any).nominalAmount ?? null,
              maxAmount: (resolvedContribRow as any).maxAmount ?? null,
            }
          : null,
      };
    }

    const contribRow = row
      ? pickContributionForRate(planContributions, row as any, {
          type: "tier_rates",
          value: String((row as any).coverageTier),
        })
      : null;
    const derived = deriveAmountsFromContribution(monthly, contribRow) || zeroEmployerSplit(monthly);
    // Gross employee-only vs dependent split for the breakdown UI (additive; no
    // effect on premium totals). Dependent portion = elected-tier gross − employee-only gross.
    const employeeOnlyRow = findTierRateRow(planRates, "employee_only");
    const employeeOnlyRate = employeeOnlyRow ? toNumber((employeeOnlyRow as any).monthlyRate, 0) : monthly;
    return {
      coverageTier: tier,
      totalMonthlyRate: Math.round(monthly * 100) / 100,
      employeePremium: Math.round(derived.employee * 100) / 100,
      employerPremium: Math.round(derived.employer * 100) / 100,
      members: [
        {
          memberType: "employee",
          age: parseAge((employee as any).dateOfBirth as any, ageAsOf),
          ageBandLabel: null,
          monthlyRate: monthly,
          employeeAmount: Math.round(derived.employee * 100) / 100,
          employerAmount: Math.round(derived.employer * 100) / 100,
          planRateId: (row as any)?.id ?? null,
        },
      ],
      tierBreakdown: {
        grossTier: Math.round(monthly * 100) / 100,
        employeeOnlyRate: Math.round(employeeOnlyRate * 100) / 100,
        dependentPortion: Math.round(Math.max(0, monthly - employeeOnlyRate) * 100) / 100,
      },
      contributionResolved: contribRow
        ? {
            method: "tier_rates",
            key: String((row as any).coverageTier),
            payer: (contribRow as any).payer,
            percentage: (contribRow as any).percentage ?? null,
            nominalAmount: (contribRow as any).nominalAmount ?? null,
            maxAmount: (contribRow as any).maxAmount ?? null,
          }
        : null,
    };
  }

  // Age-based contribution path (per-rate)
  if (structureType === "age_rates" || structureType === "wb_table") {
    const members: Array<{ memberType: MemberType; age: number | null; rateRow?: PlanRate }> =
      [];
    const employeeAge = parseAge((employee as any).dateOfBirth as any, ageAsOf) ?? 0;
    const employeeRateRow = findAgeRateRow(planRates, employeeAge, warnings) as any;
    members.push({ memberType: "employee", age: employeeAge, rateRow: employeeRateRow });

    const spouse = dependents.find((d) => isSpouseRelationship((d as any).relationship));
    if (spouse) {
      const a = parseAge((spouse as any).dateOfBirth as any, ageAsOf) ?? 0;
      members.push({ memberType: "spouse", age: a, rateRow: findAgeRateRow(planRates, a, warnings) });
    }
    const kids = dependents.filter((d) => isChildRelationship((d as any).relationship));
    for (const child of kids) {
      const a = parseAge((child as any).dateOfBirth as any, ageAsOf) ?? 0;
      members.push({ memberType: "child", age: a, rateRow: findAgeRateRow(planRates, a, warnings) });
    }

    let totalMonthly = 0;
    let sumEmployee = 0;
    let sumEmployer = 0;
    const membersOut: MemberBreakdown[] = [];
    const cappedIdx = cappedChildMemberIndices(members, planType);
    members.forEach((m, idx) => {
      const rateRow = m.rateRow;
      if (!rateRow) warnings.push(`No rate for ${m.memberType} age ${m.age} on ${planName}`);
      const billable = !cappedIdx.has(idx);
      // Capped under-21 children stay enrolled but bill $0 (ACA 3-child rule).
      const monthly = billable && rateRow ? resolveMonthlyRate(rateRow, m.memberType === "employee" && isTobaccoUser) : 0;
      totalMonthly += monthly;
      const fallbackKey = (rateRow as any)?.ageBand
        ? { type: "age_rates" as const, value: String((rateRow as any).ageBand) }
        : undefined;
      const contribRow = rateRow ? pickContributionForRate(planContributions, rateRow, fallbackKey) : null;
      const perRateDerived = deriveAmountsFromContribution(monthly, contribRow);
      if (!perRateDerived && monthly > 0) warnings.push(`No contribution matched age band '${(rateRow as any)?.ageBand}' for ${m.memberType} — employer defaulted to $0 until contributions are configured`);
      const derived = perRateDerived || zeroEmployerSplit(monthly);
      sumEmployee += derived.employee;
      sumEmployer += derived.employer;
      membersOut.push({
        memberType: m.memberType,
        age: m.age ?? null,
        ageBandLabel: (rateRow as any)?.ageBand ?? null,
        monthlyRate: monthly,
        employeeAmount: Math.round(derived.employee * 100) / 100,
        employerAmount: Math.round(derived.employer * 100) / 100,
        planRateId: (rateRow as any)?.id ?? null,
        capped: !billable,
      });
    });

    return {
      coverageTier: tier,
      totalMonthlyRate: Math.round(totalMonthly * 100) / 100,
      employeePremium: Math.round(sumEmployee * 100) / 100,
      employerPremium: Math.round(sumEmployer * 100) / 100,
      members: membersOut,
      contributionResolved: null,
    };
  }

  // Fallback when no rates
  return {
    coverageTier: tier,
    totalMonthlyRate: 0,
    employeePremium: 0,
    employerPremium: 0,
    members: [],
    contributionResolved: null,
  };
}


